import React, { useState } from 'react';
import Seo from '../components/Seo';
import { Flame, ArrowRight, Check, Mail, Users, Church, Presentation, Radio } from 'lucide-react';
import { SIGNATURE_TOPICS, PACKAGES, BRAND } from '../mock/mock';
import Reveal from '../components/Reveal';
import { useToast } from '../hooks/use-toast';
import { submitBooking } from '../lib/apiClient';

const BookDrGee = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', org: '', eventType: '', date: '', pkg: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'A few details missing', description: 'Please add your name, email and a short note about your event.' });
      return;
    }
    setSubmitting(true);
    try {
      await submitBooking(form);
      toast({ title: 'Request received', description: 'Thank you! A member of our team will reach out about availability and details.' });
      setForm({ name: '', email: '', org: '', eventType: '', date: '', pkg: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const eventTypes = [
    { icon: Users, label: 'Women’s Conference / Retreat' },
    { icon: Church, label: 'Church / Women’s Ministry' },
    { icon: Presentation, label: 'Keynote / Workshop' },
    { icon: Radio, label: 'Panel / Podcast / Media' },
  ];

  return (
    <div className="bg-fire-ink">
      <Seo title="Book Dr. Gee" path="/book" description="Invite Dr. Gee to speak at your next event, conference or retreat. Keynotes and the After the Fire™ Experience for women's ministries and organizations." />
      {/* Hero with banner image */}
      <section className="pt-24">
        <div className="relative">
          <img src="/brand/book-drgee-banner.jpg" alt="Book Dr. Gee" className="w-full h-[380px] md:h-[520px] object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-fire-ink via-fire-ink/40 to-fire-ink/60" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-10 w-full">
              <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">SPEAKER • AUTHOR • SURVIVOR • TRANSFORMER</span>
              <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-3">Book Dr. Gee</h1>
              <p className="font-cormorant italic text-fire-goldlight text-xl md:text-2xl mt-2">She doesn’t just tell a story. She brings women face-to-face with their own.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 ember-texture">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <Reveal>
            <p className="text-fire-cream/75 font-body text-lg leading-relaxed mb-5">
              Dr. Gee is a pastor, author, recording artist, motivational speaker and the visionary behind After the Fire™ — a women’s empowerment experience created for women who have survived the things they thought would destroy them.
            </p>
            <p className="text-fire-cream/60 font-body leading-relaxed">
              Whether behind a pulpit, on a conference stage, or sitting intimately with a room of women, Dr. Gee combines storytelling, biblical truth, humor, music, hard conversations and practical rebuilding strategies to create an experience women remember long after the event is over.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Event types */}
      <section className="pb-4">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {eventTypes.map((t, i) => (
            <Reveal key={i} delay={i * 60}>
              <div className="card-dark rounded-xl p-6 text-center h-full hover-lift">
                <t.icon className="w-7 h-7 text-fire-goldlight mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-body text-sm text-fire-cream/80">{t.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature topics */}
      <section className="py-24">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">SIGNATURE TOPICS</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Messages That Meet Women Where They Are</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-5">
            {SIGNATURE_TOPICS.map((t) => (
              <Reveal key={t.n} delay={(t.n % 2) * 60}>
                <div className="card-dark rounded-2xl p-7 h-full hover-lift flex gap-5">
                  <span className="font-display text-4xl text-gradient-gold leading-none shrink-0">{String(t.n).padStart(2, '0')}</span>
                  <div>
                    <h3 className="font-display text-xl text-fire-cream mb-2">{t.title}</h3>
                    <p className="text-fire-cream/60 font-body text-sm leading-relaxed">{t.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-24 bg-fire-inksoft">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">BOOKING PACKAGES</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">More Than a Speaker. It’s an Experience.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {PACKAGES.map((p, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className={`rounded-2xl p-7 h-full flex flex-col hover-lift ${p.featured ? 'bg-gradient-to-b from-fire-maroon to-fire-maroondark border border-fire-gold/40' : 'card-dark'}`}>
                  {p.featured && <span className="self-start bg-fire-gold text-fire-ink text-[10px] font-bold tracking-widest px-3 py-1 rounded-full mb-3">MOST POPULAR</span>}
                  <h3 className="font-display text-2xl text-fire-cream">{p.name}</h3>
                  <div className="mt-3 mb-1">
                    <span className="font-display text-3xl text-gradient-gold">{p.price}</span>
                    <span className="text-fire-cream/50 font-body text-sm ml-1">{p.plus}</span>
                  </div>
                  <p className="text-fire-cream/55 font-body text-sm mb-5">{p.dur}</p>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-fire-cream/75 font-body text-sm">
                        <Check className="w-4 h-4 text-fire-gold shrink-0 mt-0.5" /> {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#book-form" className={`rounded-full py-3 text-center font-semibold text-sm ${p.featured ? 'btn-gold' : 'btn-outline-gold'}`}>Request This</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Booking form */}
      <section id="book-form" className="py-24 ember-texture">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-10">
            <Flame className="w-8 h-8 text-fire-gold mx-auto mb-4 animate-flicker" strokeWidth={1.4} />
            <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">BOOKING INQUIRY</span>
            <h2 className="font-display text-3xl md:text-5xl text-fire-cream mt-3">Ready to Bring Dr. Gee to Your Event?</h2>
            <p className="text-fire-cream/60 font-body mt-4">Tell us about your event, your audience and what you want your women to walk away feeling.</p>
          </Reveal>
          <Reveal delay={100}>
            <form onSubmit={submit} className="card-dark rounded-2xl p-7 md:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Your Name *" value={form.name} onChange={(v) => update('name', v)} placeholder="Jane Doe" />
                <Field label="Email *" type="email" value={form.email} onChange={(v) => update('email', v)} placeholder="you@example.com" />
                <Field label="Organization / Church" value={form.org} onChange={(v) => update('org', v)} placeholder="Your organization" />
                <Field label="Preferred Date" type="date" value={form.date} onChange={(v) => update('date', v)} />
                <Field label="Event Type" value={form.eventType} onChange={(v) => update('eventType', v)} placeholder="Conference, retreat, church…" />
                <div>
                  <label className="block font-body text-sm text-fire-cream/70 mb-2">Package of Interest</label>
                  <select value={form.pkg} onChange={(e) => update('pkg', e.target.value)} className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream focus:outline-none focus:border-fire-gold transition-colors">
                    <option value="">Select a package</option>
                    {PACKAGES.map((p) => <option key={p.name} value={p.name}>{p.name} — {p.price}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-body text-sm text-fire-cream/70 mb-2">Tell us about your vision *</label>
                <textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={4} placeholder="What do you want your women to walk away feeling, believing and ready to do?" className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors resize-none" />
              </div>
              <button type="submit" disabled={submitting} className="btn-gold w-full rounded-full py-4 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70">
                {submitting ? 'Sending…' : (<>Request Dr. Gee <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <p className="text-center text-fire-cream/50 font-body text-sm flex items-center justify-center gap-2">
                <Mail className="w-4 h-4 text-fire-gold" /> Or email us directly: {BRAND.email}
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block font-body text-sm text-fire-cream/70 mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-fire-ink/70 border border-fire-gold/25 rounded-lg px-4 py-3 text-fire-cream placeholder:text-fire-cream/35 focus:outline-none focus:border-fire-gold transition-colors"
    />
  </div>
);

export default BookDrGee;
